/**
 * News/NewsGallery.jsx
 * ════════════════════════════════════════════════════════════════
 * Галерея фото + YouTube відео для новини (адмінська частина).
 * Вставляється всередину NewsEditor тільки в режимі редагування
 * (потрібен збережений newsId, як і обкладинка).
 *
 * Кожен файл має підпис (caption) — редагується прямо під
 * мініатюрою, зберігається по blur/Enter.
 *
 * Props:
 *   scope  {string}
 *   kgId   {string?}
 *   newsId {string}
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faPlay } from '@fortawesome/free-solid-svg-icons';
import { faYoutube as faYtBrand } from '@fortawesome/free-brands-svg-icons';
import { toast } from 'react-toastify';
import { constr_uri } from '../../../../../services/apiClient';
import PhotoViewer from '../../../../../components/PhotoViewer/PhotoViewer';
import FileUploader from '../../../../../components/FileUpload/FileUploader';
import { newsAPI, base64ToFile } from './newsAPI';

var MAX_GALLERY_FILES = 30;

// ── Одна плитка галереї (фото/відео + підпис) ─────────────────────────────────

var GalleryTile = function({ file, docId, scope, kgId, newsId, onDeleted, onView }) {
    var isVideo = file.source === 'youtube';
    var thumbSrc = isVideo
        ? file.thumbnail_url
        : constr_uri('docfile?doc_id=' + docId + '&file_id=' + file._id);

    var [caption, setCaption] = useState(file.caption || '');
    var [saving, setSaving]   = useState(false);

    var saveCaption = async function() {
        if (caption === (file.caption || '')) return;
        setSaving(true);
        try {
            await newsAPI.updateGalleryCaption(scope, kgId, newsId, file._id, caption);
        } catch (err) {
            toast.error('Не вдалося зберегти підпис');
        } finally {
            setSaving(false);
        }
    };

    var handleDelete = async function() {
        if (!window.confirm('Видалити цей файл з галереї?')) return;
        try {
            await newsAPI.deleteGalleryFile(scope, kgId, newsId, file._id);
            onDeleted(file._id);
        } catch (err) {
            toast.error('Помилка видалення');
        }
    };

    return (
        <div>
            <div
                style={{
                    position: 'relative', paddingBottom: '100%',
                    borderRadius: 8, overflow: 'hidden',
                    background: '#f3f4f6', cursor: 'pointer',
                    border: '1px solid #e5e7eb',
                }}
                onClick={function() { onView(file); }}
            >
                {thumbSrc && (
                    <img
                        src={thumbSrc} alt=""
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                )}
                {isVideo && (
                    <div style={{
                        position: 'absolute', inset: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(0,0,0,0.25)',
                    }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: '50%',
                            background: 'rgba(255,0,0,0.9)', color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <FontAwesomeIcon icon={faPlay} style={{ fontSize: 12, marginLeft: 2 }} />
                        </div>
                    </div>
                )}
                <button
                    type="button"
                    onClick={function(e) { e.stopPropagation(); handleDelete(); }}
                    style={{
                        position: 'absolute', top: 4, right: 4,
                        width: 22, height: 22, borderRadius: '50%',
                        background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10,
                    }}
                >
                    <FontAwesomeIcon icon={faTrash} />
                </button>
            </div>

            {/* Підпис */}
            <input
                type="text"
                value={caption}
                onChange={function(e) { setCaption(e.target.value); }}
                onBlur={saveCaption}
                onKeyDown={function(e) { if (e.key === 'Enter') e.target.blur(); }}
                onClick={function(e) { e.stopPropagation(); }}
                placeholder="Підпис..."
                maxLength={200}
                style={{
                    width: '100%', marginTop: 4,
                    fontSize: 11, color: '#6b7280',
                    border: 'none', borderBottom: '1px solid transparent',
                    padding: '2px 0', outline: 'none', background: 'transparent',
                    opacity: saving ? 0.5 : 1,
                }}
                onFocus={function(e) { e.target.style.borderBottomColor = '#dee2e6'; }}
            />
        </div>
    );
};

// ════════════════════════════════════════════════════════════════
// ГОЛОВНИЙ КОМПОНЕНТ
// ════════════════════════════════════════════════════════════════

var NewsGallery = function({ scope, kgId, newsId }) {
    var [files, setFiles]     = useState([]);
    var [docId, setDocId]     = useState(null);
    var [loading, setLoading] = useState(true);
    var [uploading, setUploading] = useState(false);
    var [videoUrl, setVideoUrl]   = useState('');
    var [videoCaption, setVideoCaption] = useState('');
    var [addingVideo, setAddingVideo] = useState(false);
    var [viewer, setViewer]   = useState(null);

    var load = function() {
        setLoading(true);
        newsAPI.getGallery(scope, kgId, newsId)
            .then(function(result) {
                setFiles(result.files || []);
                setDocId(result.docId);
            })
            .catch(function() { toast.error('Помилка завантаження галереї'); })
            .finally(function() { setLoading(false); });
    };

    useEffect(load, [newsId]);

    // FileUploader — контрольований, value завжди [] (транзитний вибір).
    // Одразу після вибору файлів вантажимо кожен послідовно на сервер,
    // потім оновлюємо сітку з реальних даних сервера.
    var handlePick = async function(picked) {
        if (!picked || !picked.length) return;

        setUploading(true);
        for (var i = 0; i < picked.length; i++) {
            var pf = picked[i];
            try {
                var file = base64ToFile(pf.file, pf.name, pf.type);
                await newsAPI.uploadGalleryPhoto(scope, kgId, newsId, file);
            } catch (err) {
                toast.error('Помилка завантаження ' + pf.name);
            }
        }
        setUploading(false);
        load();
    };

    var handleAddVideo = async function() {
        if (!videoUrl.trim()) return;
        setAddingVideo(true);
        try {
            await newsAPI.addGalleryVideo(scope, kgId, newsId, videoUrl.trim(), videoCaption.trim());
            setVideoUrl('');
            setVideoCaption('');
            toast.success('Відео додано');
            load();
        } catch (err) {
            toast.error('Не вдалося додати відео. Перевірте посилання.');
        } finally {
            setAddingVideo(false);
        }
    };

    var handleFileDeleted = function(fileId) {
        setFiles(function(prev) { return prev.filter(function(f) { return f._id !== fileId; }); });
    };

    var handleView = function(file) {
        var isVideo = file.source === 'youtube';
        setViewer({
            src:   isVideo ? file.youtube_url : constr_uri('docfile?doc_id=' + docId + '&file_id=' + file._id),
            name:  file.caption || file.name,
            mtype: isVideo ? 'video/youtube' : file.mtype,
        });
    };

    return (
        <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#111827', display: 'block', marginBottom: 10 }}>
                Галерея фото та відео
            </label>

            {/* Сітка файлів */}
            {loading ? (
                <div className="text-center py-3"><Spinner animation="border" size="sm" variant="primary" /></div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: 12, marginBottom: 14,
                }}>
                    {files.map(function(file) {
                        return (
                            <GalleryTile
                                key={file._id}
                                file={file}
                                docId={docId}
                                scope={scope}
                                kgId={kgId}
                                newsId={newsId}
                                onDeleted={handleFileDeleted}
                                onView={handleView}
                            />
                        );
                    })}
                </div>
            )}

            {/* Додавання фото — FileUploader для вибору + прев'ю,
                кожен файл вантажиться на сервер одразу після вибору */}
            {files.length < MAX_GALLERY_FILES && (
                <div style={{ marginBottom: 14 }}>
                    <FileUploader
                        label=""
                        description={'Фото JPG, PNG. До ' + (MAX_GALLERY_FILES - files.length) + ' файлів, максимум 10 МБ кожен.'}
                        accept="image/*"
                        multiple={true}
                        maxCount={MAX_GALLERY_FILES - files.length}
                        maxSize={10 * 1024 * 1024}
                        value={[]}
                        onChange={handlePick}
                        asBase64={true}
                        editable={!uploading}
                        disabled={uploading}
                    />
                    {uploading && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                            <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                            Завантаження на сервер...
                        </div>
                    )}
                </div>
            )}

            {/* Додавання YouTube відео */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <FontAwesomeIcon icon={faYtBrand} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#ff0000', fontSize: 15 }} />
                        <input
                            type="text"
                            value={videoUrl}
                            onChange={function(e) { setVideoUrl(e.target.value); }}
                            placeholder="Посилання на YouTube відео..."
                            style={{
                                width: '100%', height: 38, paddingLeft: 34, paddingRight: 10,
                                border: '1px solid #ced4da', borderRadius: 8, fontSize: 13,
                            }}
                        />
                    </div>
                    <button
                        type="button" onClick={handleAddVideo}
                        disabled={addingVideo || !videoUrl.trim()}
                        style={{
                            height: 38, padding: '0 16px', borderRadius: 8, border: 'none',
                            background: '#0069ff', color: '#fff', fontSize: 13, fontWeight: 600,
                            cursor: 'pointer', opacity: !videoUrl.trim() ? 0.5 : 1,
                            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
                        }}
                    >
                        {addingVideo ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} /> : <FontAwesomeIcon icon={faPlus} />}
                        Додати
                    </button>
                </div>
                {videoUrl.trim() && (
                    <input
                        type="text"
                        value={videoCaption}
                        onChange={function(e) { setVideoCaption(e.target.value); }}
                        onKeyDown={function(e) { if (e.key === 'Enter') handleAddVideo(); }}
                        placeholder="Підпис до відео (необов'язково)..."
                        maxLength={200}
                        style={{
                            height: 34, padding: '0 10px',
                            border: '1px solid #ced4da', borderRadius: 8, fontSize: 12,
                        }}
                    />
                )}
            </div>

            {/* Перегляд фото / відео */}
            {viewer && (
                <PhotoViewer
                    src={viewer.src}
                    name={viewer.name}
                    mtype={viewer.mtype}
                    onClose={function() { setViewer(null); }}
                />
            )}
        </div>
    );
};

export default NewsGallery;